import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Package, Truck, CheckCircle, XCircle, PlusCircle, LayoutGrid, List, ChevronLeft, ChevronRight, ChevronDown, ChevronsLeft, ChevronsRight,
  MapPin, Globe, Loader2, GitBranch, Calendar, Search, X, ArrowRight, Clock, CircleDot,
  MoreVertical, Copy, FileText, Ban, Eye, Bookmark, BookmarkPlus, Trash2, ArrowUpDown, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getVehicleTypeLabel } from '../constants/vehicleTypes';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import AdvancedSearchPanel from '../components/AdvancedSearchPanel';
import KpiCard from '../components/ui-kit/KpiCard';
import ProgressBar from '../components/ui-kit/ProgressBar';
import { StatusLegendDrawer } from '../components/ui-kit/StatusBadge';

const token = 'cookie-auth';

// SEC-003 — sentinelle pour passer les guards `if (!token)`; le cookie HttpOnly est envoyé via axios withCredentials.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: "'Manrope', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

const SAVED_VIEWS_KEY = 'baobab_client_ops_saved_views_v1';

const shortenAddress = (addr) => (addr ? addr.split(',')[0].trim() : '-');

const STATUS_STYLES = {
  demande_enregistree: { label: 'Enregistrée', cls: 'bg-slate-100 text-slate-700 border-slate-200', step: 0 },
  en_attente_de_devis: { label: 'Attente devis', cls: 'bg-slate-100 text-slate-700 border-slate-200', step: 0 },
  en_attente_validation_client: { label: 'À valider', cls: 'bg-amber-50 text-amber-700 border-amber-200', step: 1 },
  en_negociation: { label: 'Négociation', cls: 'bg-amber-50 text-amber-700 border-amber-200', step: 1 },
  devis_accepte: { label: 'Devis accepté', cls: 'bg-blue-50 text-blue-700 border-blue-200', step: 2 },
  planifiee: { label: 'Planifiée', cls: 'bg-blue-50 text-blue-700 border-blue-200', step: 2 },
  prete_au_chargement: { label: 'Prête', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', step: 3 },
  en_cours_de_chargement: { label: 'Chargement', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', step: 3 },
  charge_en_route: { label: 'En route', cls: 'bg-violet-50 text-violet-700 border-violet-200', step: 4 },
  livree: { label: 'Livrée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', step: 5 },
  en_attente_paiement: { label: 'Attente paiement', cls: 'bg-orange-50 text-orange-700 border-orange-200', step: 5 },
  payee: { label: 'Payée', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', step: 6 },
  terminee: { label: 'Terminée', cls: 'bg-zinc-100 text-zinc-700 border-zinc-200', step: 6 },
  cancelled: { label: 'Annulée', cls: 'bg-red-50 text-red-600 border-red-200', step: -1 },
  annulee_admin: { label: 'Annulée', cls: 'bg-red-50 text-red-600 border-red-200', step: -1 },
  annulee_par_client: { label: 'Annulée', cls: 'bg-red-50 text-red-600 border-red-200', step: -1 },
};

const getStatus = (s) => STATUS_STYLES[s] || { label: s, cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', step: 0 };

const StatusPill = ({ status }) => {
  const st = getStatus(status);
  return <Badge className={`text-[11px] h-5 px-2.5 border font-medium rounded-full ${st.cls}`}>{st.label}</Badge>;
};

const TABS = [
  { id: 'all', label: 'Toutes', icon: null },
  { id: 'preparation', label: 'Préparation', icon: Clock },
  { id: 'execution', label: 'En cours', icon: Truck },
  { id: 'terminees', label: 'Terminées', icon: CheckCircle },
  { id: 'annulees', label: 'Annulées', icon: XCircle },
];

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Date (récent → ancien)' },
  { value: 'date_asc', label: 'Date (ancien → récent)' },
  { value: 'price_desc', label: 'Tarif (élevé → bas)' },
  { value: 'price_asc', label: 'Tarif (bas → élevé)' },
  { value: 'distance_desc', label: 'Distance (longue → courte)' },
  { value: 'distance_asc', label: 'Distance (courte → longue)' },
];

const preparationStatuses = ['demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client', 'en_negociation', 'devis_accepte'];
const executionStatuses = ['planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree'];
const termineesStatuses = ['payee', 'terminee'];
const annuleesStatuses = ['cancelled', 'annulee_admin', 'annulee_par_client'];

/** Compute % evolution between two counts. Returns 0 if both are 0 (stable). */
const computeChange = (current, previous) => {
  if (!previous || previous === 0) {
    if (current === 0) return 0;
    return current > 0 ? 100 : null;
  }
  return Math.round(((current - previous) / previous) * 100);
};

const ClientOperations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const permissions = useClientPermissions();

  const [allOperations, setAllOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  // Filtre statut "fin-de-pipeline" (en_retard) appliqué via query param depuis le dashboard
  const [statusQueryFilter, setStatusQueryFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState('list');
  const [expandedOperations, setExpandedOperations] = useState({});
  const [childrenCache, setChildrenCache] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [advancedFilteredOps, setAdvancedFilteredOps] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc');

  // Saved views (Lot 5)
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const hasCompany = user?.companyName && user.companyName.trim() !== '';

  // ---- Lot 1: Stats + Évolution mois précédent ----
  const stats = useMemo(() => {
    const now = new Date();
    const startCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

    const inCurrent = (op) => {
      const d = new Date(op.createdAt || op.created_at || op.deliveryDate);
      return d >= startCurrentMonth;
    };
    const inPrev = (op) => {
      const d = new Date(op.createdAt || op.created_at || op.deliveryDate);
      return d >= startPrevMonth && d <= endPrevMonth;
    };

    const cur = {
      total: allOperations.filter(inCurrent).length,
      enPreparation: allOperations.filter(op => inCurrent(op) && preparationStatuses.includes(op.status)).length,
      enExecution: allOperations.filter(op => inCurrent(op) && executionStatuses.includes(op.status)).length,
      terminees: allOperations.filter(op => inCurrent(op) && termineesStatuses.includes(op.status)).length,
    };
    const prev = {
      total: allOperations.filter(inPrev).length,
      enPreparation: allOperations.filter(op => inPrev(op) && preparationStatuses.includes(op.status)).length,
      enExecution: allOperations.filter(op => inPrev(op) && executionStatuses.includes(op.status)).length,
      terminees: allOperations.filter(op => inPrev(op) && termineesStatuses.includes(op.status)).length,
    };

    return {
      total: allOperations.length,
      enPreparation: allOperations.filter(op => preparationStatuses.includes(op.status)).length,
      enExecution: allOperations.filter(op => executionStatuses.includes(op.status)).length,
      terminees: allOperations.filter(op => termineesStatuses.includes(op.status)).length,
      annulees: allOperations.filter(op => annuleesStatuses.includes(op.status)).length,
      changes: {
        total: computeChange(cur.total, prev.total),
        enPreparation: computeChange(cur.enPreparation, prev.enPreparation),
        enExecution: computeChange(cur.enExecution, prev.enExecution),
        terminees: computeChange(cur.terminees, prev.terminees),
      },
    };
  }, [allOperations]);

  useEffect(() => {
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/transport-requests?limit=100000`);
        setAllOperations(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ---- Synchro query param `?status=` ↔ onglet/filtre (deep-link depuis dashboard) ----
  // Mapping : en_cours→execution, livree→terminees, en_retard→filtre dédié, all/null→all
  useEffect(() => {
    const status = (searchParams.get('status') || '').toLowerCase();
    if (!status) {
      setActiveTab('all');
      setStatusQueryFilter(null);
      return;
    }
    if (status === 'en_cours') {
      setActiveTab('execution');
      setStatusQueryFilter(null);
    } else if (status === 'livree' || status === 'livrees' || status === 'livrée' || status === 'terminees') {
      setActiveTab('terminees');
      setStatusQueryFilter(null);
    } else if (status === 'en_retard' || status === 'late') {
      setActiveTab('all');
      setStatusQueryFilter('en_retard');
    } else if (status === 'preparation' || status === 'en_preparation') {
      setActiveTab('preparation');
      setStatusQueryFilter(null);
    } else if (status === 'annulees' || status === 'annulee' || status === 'cancelled') {
      setActiveTab('annulees');
      setStatusQueryFilter(null);
    } else {
      // statut brut inconnu : on tente de l'utiliser comme filtre exact
      setActiveTab('all');
      setStatusQueryFilter(status);
    }
    setCurrentPage(1);
  }, [searchParams]);

  // Load saved views from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_VIEWS_KEY);
      if (raw) setSavedViews(JSON.parse(raw));
    } catch (e) { console.warn('Failed to load saved views', e); }
  }, []);

  const persistViews = (views) => {
    setSavedViews(views);
    try { localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(views)); } catch {}
  };

  const handleSaveView = () => {
    if (!newViewName.trim()) {
      toast.error('Veuillez nommer la vue');
      return;
    }
    const view = {
      id: `view_${Date.now()}`,
      name: newViewName.trim(),
      activeTab, sortBy, ownershipFilter, viewMode, searchQuery,
      createdAt: new Date().toISOString(),
    };
    const next = [...savedViews, view];
    persistViews(next);
    setActiveViewId(view.id);
    setShowSaveDialog(false);
    setNewViewName('');
    toast.success(`Vue "${view.name}" enregistrée`);
  };

  const applyView = (view) => {
    setActiveTab(view.activeTab || 'all');
    setSortBy(view.sortBy || 'date_desc');
    setOwnershipFilter(view.ownershipFilter || 'all');
    setViewMode(view.viewMode || 'list');
    setSearchQuery(view.searchQuery || '');
    setActiveViewId(view.id);
    toast.success(`Vue "${view.name}" appliquée`);
  };

  const deleteView = (viewId, e) => {
    e?.stopPropagation();
    const view = savedViews.find(v => v.id === viewId);
    persistViews(savedViews.filter(v => v.id !== viewId));
    if (activeViewId === viewId) setActiveViewId(null);
    if (view) toast.success(`Vue "${view.name}" supprimée`);
  };

  const fetchChildren = async (parentId) => {
    if (childrenCache[parentId]) return;
    setLoadingChildren(p => ({ ...p, [parentId]: true }));
    try {
      const res = await axios.get(`${API}/operations/${parentId}/children`);
      setChildrenCache(p => ({ ...p, [parentId]: res.data.items || [] }));
    } catch (e) { console.error(e); }
    finally { setLoadingChildren(p => ({ ...p, [parentId]: false })); }
  };

  const toggleExpand = async (e, opId) => {
    e.stopPropagation();
    if (expandedOperations[opId]) {
      setExpandedOperations(p => ({ ...p, [opId]: false }));
    } else {
      await fetchChildren(opId);
      setExpandedOperations(p => ({ ...p, [opId]: true }));
    }
  };

  // ---- Filtering + Sorting ----
  const filtered = useMemo(() => {
    let ops = advancedFilteredOps !== null ? advancedFilteredOps : allOperations;
    if (hasCompany && ownershipFilter === 'mine') ops = ops.filter(op => op.createdById === user?.id || op.client_id === user?.id);
    if (activeTab === 'preparation') ops = ops.filter(op => preparationStatuses.includes(op.status));
    else if (activeTab === 'execution') ops = ops.filter(op => executionStatuses.includes(op.status));
    else if (activeTab === 'terminees') ops = ops.filter(op => termineesStatuses.includes(op.status));
    else if (activeTab === 'annulees') ops = ops.filter(op => annuleesStatuses.includes(op.status));
    // Filtre additionnel via query param (?status=en_retard, ou statut brut)
    if (statusQueryFilter) {
      ops = ops.filter(op => (op.status || '').toLowerCase() === statusQueryFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      ops = ops.filter(op =>
        op.departure?.toLowerCase().includes(q) || op.arrival?.toLowerCase().includes(q) ||
        op.vehicleType?.toLowerCase().includes(q) || op.operationNumber?.toLowerCase().includes(q) ||
        op.merchandiseType?.toLowerCase().includes(q) || op.createdByName?.toLowerCase().includes(q)
      );
    }

    // Sort
    const sorted = [...ops];
    const tsOf = (op) => {
      const d = op.createdAt || op.created_at || op.deliveryDate;
      return d ? new Date(d).getTime() : 0;
    };
    switch (sortBy) {
      case 'date_asc': sorted.sort((a, b) => tsOf(a) - tsOf(b)); break;
      case 'price_desc': sorted.sort((a, b) => (b.estimatedPrice || 0) - (a.estimatedPrice || 0)); break;
      case 'price_asc': sorted.sort((a, b) => (a.estimatedPrice || 0) - (b.estimatedPrice || 0)); break;
      case 'distance_desc': sorted.sort((a, b) => (b.distance_km || 0) - (a.distance_km || 0)); break;
      case 'distance_asc': sorted.sort((a, b) => (a.distance_km || 0) - (b.distance_km || 0)); break;
      case 'date_desc':
      default: sorted.sort((a, b) => tsOf(b) - tsOf(a)); break;
    }
    return sorted;
  }, [allOperations, advancedFilteredOps, activeTab, searchQuery, ownershipFilter, user?.id, hasCompany, sortBy, statusQueryFilter]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [activeTab, searchQuery, itemsPerPage, sortBy]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const fmtShort = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '-';

  const tabCounts = { all: stats.total, preparation: stats.enPreparation, execution: stats.enExecution, terminees: stats.terminees, annulees: stats.annulees };

  // ---- Lot 4: Row actions ----
  const handleDuplicate = useCallback((op, e) => {
    e?.stopPropagation();
    try {
      // Use TransportForm-compatible keys (cf. TransportForm.jsx initialData mapping)
      const draft = {
        departureAddress: op.departure || '',
        arrivalAddress: op.arrival || '',
        departureCoordinates: op.departureCoordinates || op.departure_coordinates || null,
        arrivalCoordinates: op.arrivalCoordinates || op.arrival_coordinates || null,
        vehicleType: op.vehicleType || '',
        operationType: op.operationType || '',
        merchandiseType: op.merchandiseType || '',
        containerType: op.containerType || '',
        weight: op.weight || '',
        numberOfTrucks: op.numberOfTrucks || 1,
        notes: op.notes || op.specialInstructions || '',
      };
      sessionStorage.setItem('baobab_operation_draft', JSON.stringify(draft));
      toast.success('Opération dupliquée — modifiez puis envoyez');
      navigate('/nouvelle-demande');
    } catch (err) {
      toast.error('Erreur lors de la duplication');
    }
  }, [navigate]);

  const handleCancel = useCallback(async (op, e) => {
    e?.stopPropagation();
    const reason = window.prompt('Motif d\'annulation (obligatoire) :');
    if (!reason || !reason.trim()) {
      toast.error('Motif requis pour annuler');
      return;
    }
    try {
      await axios.post(`${API}/operation-status/${op.id}/change`, {
        new_status: 'annulee_par_client',
        reason: reason.trim(),
      });
      setAllOperations(prev => prev.map(o => o.id === op.id ? { ...o, status: 'annulee_par_client' } : o));
      toast.success('Opération annulée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  }, []);

  const canCancel = (op) => {
    if (!permissions.canCreateOperations) return false;
    return ![...termineesStatuses, ...annuleesStatuses, 'charge_en_route', 'livree', 'en_attente_paiement'].includes(op.status);
  };

  const RowActionsMenu = ({ op }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button
          className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
          data-testid={`row-actions-${op.id}`}
          aria-label="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs">Actions rapides</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => { e.stopPropagation(); navigate(`/operations/${op.id}`); }}
          data-testid={`action-view-${op.id}`}
        >
          <Eye className="w-4 h-4 mr-2" />Voir le détail
        </DropdownMenuItem>
        {permissions.canCreateOperations && (
          <DropdownMenuItem
            onClick={(e) => handleDuplicate(op, e)}
            data-testid={`action-duplicate-${op.id}`}
          >
            <Copy className="w-4 h-4 mr-2" />Dupliquer
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={(e) => { e.stopPropagation(); navigate(`/operations/${op.id}?tab=documents`); }}
          data-testid={`action-docs-${op.id}`}
        >
          <FileText className="w-4 h-4 mr-2" />Documents
        </DropdownMenuItem>
        {canCancel(op) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => handleCancel(op, e)}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid={`action-cancel-${op.id}`}
            >
              <Ban className="w-4 h-4 mr-2" />Annuler
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen" data-testid="client-operations">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight" style={heading}>
            Mes opérations
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{stats.total} opération{stats.total > 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusLegendDrawer />
          {permissions.canCreateOperations && (
            <Button onClick={() => navigate('/nouvelle-demande')} className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm gap-2" data-testid="new-request-btn-operations">
              <PlusCircle className="w-4 h-4" />Nouvelle demande
            </Button>
          )}
        </div>
      </div>

      {/* Lot 1: KPI Summary avec évolution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <KpiCard
          label="Total"
          value={stats.total}
          icon={Package}
          variant="emerald"
          change={stats.changes.total}
          changeLabel="vs mois dernier"
          testId="stat-total"
        />
        <KpiCard
          label="En préparation"
          value={stats.enPreparation}
          icon={Clock}
          variant="amber"
          change={stats.changes.enPreparation}
          changeLabel="vs mois dernier"
          testId="stat-en-preparation"
        />
        <KpiCard
          label="En cours"
          value={stats.enExecution}
          icon={Truck}
          variant="blue"
          change={stats.changes.enExecution}
          changeLabel="vs mois dernier"
          testId="stat-en-cours"
        />
        <KpiCard
          label="Terminées"
          value={stats.terminees}
          icon={CheckCircle}
          variant="green"
          change={stats.changes.terminees}
          changeLabel="vs mois dernier"
          testId="stat-terminees"
        />
      </div>

      {/* Lot 5: Saved Views chips */}
      {savedViews.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="saved-views-bar">
          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Vues :</span>
          {savedViews.map(v => (
            <button
              key={v.id}
              onClick={() => applyView(v)}
              className={`group flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                activeViewId === v.id
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
              }`}
              data-testid={`view-${v.id}`}
            >
              <Bookmark className="w-3 h-3" />
              {v.name}
              <Trash2
                className="w-3 h-3 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                onClick={(e) => deleteView(v.id, e)}
              />
            </button>
          ))}
        </div>
      )}

      {/* Lot 2: Toolbar unifié — sticky */}
      <div className="bg-white rounded-xl border border-zinc-100 shadow-[0_1px_3px_0_rgb(0,0,0,0.04)] mb-6 lg:sticky lg:top-2 lg:z-20">
        {/* Row 1: search + filters + sort + actions */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-100 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 group-focus-within:text-emerald-600 transition-colors" />
            <Input
              placeholder="Rechercher trajet, n° dossier, marchandise…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 h-9 text-sm rounded-lg border-zinc-200 focus:border-emerald-500 focus:ring-emerald-500/20"
              data-testid="operations-search-input"
            />
            {searchQuery && (
              <button className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" onClick={() => setSearchQuery('')}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {hasCompany && (
            <Select value={ownershipFilter} onValueChange={setOwnershipFilter}>
              <SelectTrigger className="w-[180px] h-9 text-sm rounded-lg" data-testid="ownership-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute l'agence</SelectItem>
                <SelectItem value="mine">Mes opérations</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Advanced filters + Export CSV + Reset (provided by AdvancedSearchPanel) */}
          <AdvancedSearchPanel
            operations={allOperations}
            onFilter={(results) => setAdvancedFilteredOps(results)}
            onReset={() => setAdvancedFilteredOps(null)}
          />

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[210px] h-9 text-sm rounded-lg" data-testid="sort-select">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Save view */}
            <DropdownMenu open={showSaveDialog} onOpenChange={setShowSaveDialog}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 h-9" data-testid="save-view-btn">
                  <BookmarkPlus className="w-4 h-4" />Enregistrer la vue
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 p-3">
                <DropdownMenuLabel className="text-xs px-0 pb-2">Nommer cette vue</DropdownMenuLabel>
                <Input
                  placeholder="Ex: Mes urgences"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveView(); }}
                  className="h-8 text-sm mb-2"
                  autoFocus
                  data-testid="new-view-name-input"
                />
                <p className="text-[11px] text-zinc-400 mb-3">Sauvegarde : tri, filtres rapides, recherche.</p>
                <Button
                  size="sm"
                  onClick={handleSaveView}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5 h-8"
                  data-testid="confirm-save-view-btn"
                >
                  <Save className="w-3.5 h-3.5" />Enregistrer
                </Button>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* List/grid toggle */}
            <div className="flex items-center bg-zinc-100 rounded-lg p-0.5">
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-400 hover:text-zinc-600'}`} data-testid="view-list-btn"><List className="w-4 h-4" /></button>
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-400 hover:text-zinc-600'}`} data-testid="view-grid-btn"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Row 2: Status pills */}
        <div className="flex items-center gap-1.5 px-5 py-2.5 overflow-x-auto">
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Si l'utilisateur clique manuellement, on efface le query param et le filtre statut
                  setStatusQueryFilter(null);
                  if (searchParams.get('status')) {
                    const next = new URLSearchParams(searchParams);
                    next.delete('status');
                    setSearchParams(next, { replace: true });
                  }
                }}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  active ? 'bg-emerald-600 text-white shadow-sm' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
                {tab.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-zinc-200/60 text-zinc-500'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {paginated.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-zinc-100">
          <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4"><Package className="h-8 w-8 text-zinc-300" /></div>
          <p className="text-base font-medium text-zinc-500" style={heading}>{searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Aucune opération'}</p>
          <p className="text-sm text-zinc-400 mt-1">
            {searchQuery ? 'Essayez un autre terme de recherche' : 'Vos opérations apparaîtront ici'}
          </p>
          {!searchQuery && permissions.canCreateOperations && (
            <Button onClick={() => navigate('/nouvelle-demande')} className="mt-5 bg-emerald-600 hover:bg-emerald-700">Créer une demande</Button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-[0_1px_3px_0_rgb(0,0,0,0.04)] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/80 hover:bg-zinc-50/80 border-b border-zinc-200">
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 pl-5 w-[240px]">Référence</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Trajet</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[130px]">Statut</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[120px]">Véhicule</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[80px]">Distance</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[100px]">Date</TableHead>
                {hasCompany && <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[110px]">Créé par</TableHead>}
                {permissions.canViewPrices && <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[110px]">Tarif</TableHead>}
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[140px]">Avancement</TableHead>
                <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map(op => {
                const hasChildren = op.isParentOperation || (op.childrenCount || 0) > 0;
                const isExpanded = expandedOperations[op.id];
                const children = childrenCache[op.id] || [];
                const st = getStatus(op.status);
                return (
                  <React.Fragment key={op.id}>
                    <TableRow
                      className="cursor-pointer transition-colors duration-150 hover:bg-zinc-50/80 h-14 group"
                      onClick={() => op.isParentOperation && op.contractId ? navigate(`/mes-contrats/${op.contractId}`) : navigate(`/operations/${op.id}`)}
                      data-testid={`operation-row-${op.id}`}
                    >
                      <TableCell className="pl-5 py-3">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {hasChildren && (
                            <button onClick={e => toggleExpand(e, op.id)} className="p-0.5 rounded hover:bg-zinc-100 transition-colors" data-testid={`expand-row-btn-${op.id}`}>
                              {loadingChildren[op.id] ? <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" /> : isExpanded ? <ChevronDown className="w-4 h-4 text-emerald-600" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                            </button>
                          )}
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-zinc-700 font-mono bg-zinc-100 px-2 py-1 rounded whitespace-nowrap">{op.operationNumber || '-'}</span>
                            {op.externalRef && (
                              <span className="text-[10px] text-emerald-700 font-mono" title={op.externalRefLabel || 'Référence externe'} data-testid={`ext-ref-${op.id}`}>
                                {op.externalRef}
                              </span>
                            )}
                          </div>
                          {hasChildren && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5"><GitBranch className="w-3 h-3" />{op.childrenCount || 0}</span>}
                          {op.contractId && <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full font-medium">Contrat</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-zinc-800 truncate max-w-[140px]">{shortenAddress(op.departure)}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-zinc-300 shrink-0" />
                          <span className="text-sm text-zinc-600 truncate max-w-[140px]">{shortenAddress(op.arrival)}</span>
                          {op.intraInterLand === 'INTRALAND' ? <MapPin className="w-3 h-3 text-zinc-300 shrink-0" /> : op.intraInterLand ? <Globe className="w-3 h-3 text-zinc-300 shrink-0" /> : null}
                        </div>
                      </TableCell>
                      <TableCell className="py-3"><StatusPill status={op.status} /></TableCell>
                      <TableCell className="py-3">
                        <div className="text-xs">
                          <span className="text-zinc-700 font-medium">{getVehicleTypeLabel(op.vehicleType)}</span>
                          <span className="text-zinc-400 ml-1">{op.weight}T</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-3"><span className="text-xs text-zinc-500" style={mono}>{op.distance_km ? `${Math.round(op.distance_km)} km` : '—'}</span></TableCell>
                      <TableCell className="py-3"><span className="text-xs text-zinc-500">{fmtDate(op.deliveryDate)}</span></TableCell>
                      {hasCompany && (
                        <TableCell className="py-3">
                          <span className="text-xs text-zinc-500 truncate block max-w-[100px]">{op.createdByName || [op.firstName, op.lastName].filter(Boolean).join(' ') || '-'}</span>
                        </TableCell>
                      )}
                      {permissions.canViewPrices && (
                        <TableCell className="py-3">
                          <span className="text-sm font-semibold text-zinc-800" style={mono}>{op.estimatedPrice?.toLocaleString('fr-FR') || '-'}</span>
                          {op.estimatedPrice && <span className="text-[10px] text-zinc-400 ml-0.5">XOF</span>}
                        </TableCell>
                      )}
                      {/* Lot 3: ProgressBar */}
                      <TableCell className="py-3">
                        <ProgressBar step={st.step} total={6} />
                      </TableCell>
                      {/* Lot 4: Row actions */}
                      <TableCell className="py-3 pr-3">
                        <RowActionsMenu op={op} />
                      </TableCell>
                    </TableRow>
                    {hasChildren && isExpanded && children.map((child, idx) => (
                      <TableRow key={child.id} className="bg-zinc-50/40 hover:bg-zinc-100/60 border-l-2 border-l-emerald-500 cursor-pointer" onClick={() => child.contractId ? navigate(`/mes-contrats/${child.contractId}/transferts/${child.id}`) : navigate(`/operations/${child.id}`)} data-testid={`child-row-${child.id}`}>
                        <TableCell className="pl-12 py-2" colSpan={99}>
                          <div className="flex items-center gap-4 text-sm">
                            <GitBranch className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="text-[11px] text-zinc-500 bg-white px-2 py-0.5 rounded" style={mono}>{child.runNumber || `Transfert ${idx + 1}`}</span>
                            <div className="flex items-center gap-1.5 text-zinc-600">
                              <span className="text-xs">{child.stops?.[0]?.address?.split(',')[0] || '-'}</span>
                              <ArrowRight className="w-3 h-3 text-zinc-300" />
                              <span className="text-xs">{child.stops?.[child.stops?.length - 1]?.address?.split(',')[0] || '-'}</span>
                            </div>
                            {child.date && <span className="text-[11px] text-zinc-400" style={mono}>{fmtShort(child.date)}</span>}
                            <StatusPill status={child.status} />
                            {child.plannedDriverName && <span className="text-xs text-zinc-400">{child.plannedDriverName}</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        /* Grid view */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginated.map(op => {
            const hasChildren = op.isParentOperation || (op.childrenCount || 0) > 0;
            const st = getStatus(op.status);
            return (
              <div
                key={op.id}
                onClick={() => op.isParentOperation && op.contractId ? navigate(`/mes-contrats/${op.contractId}`) : navigate(`/operations/${op.id}`)}
                className="bg-white rounded-xl border border-zinc-100 p-5 shadow-[0_1px_3px_0_rgb(0,0,0,0.04)] hover:shadow-md hover:border-zinc-200 transition-all duration-200 cursor-pointer group relative"
                data-testid={`operation-card-${op.id}`}
              >
                {/* Card header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded">{op.operationNumber || '-'}</span>
                    {hasChildren && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5"><GitBranch className="w-3 h-3" />{op.childrenCount || 0}</span>}
                    {op.contractId && <span className="text-[9px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full font-medium">Contrat</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <StatusPill status={op.status} />
                    <RowActionsMenu op={op} />
                  </div>
                </div>

                {/* Route */}
                <div className="mb-4">
                  <div className="flex items-start gap-2.5">
                    <div className="flex flex-col items-center pt-1">
                      <CircleDot className="w-3.5 h-3.5 text-emerald-600" />
                      <div className="w-px h-6 bg-zinc-200 my-0.5" />
                      <MapPin className="w-3.5 h-3.5 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-800 truncate leading-tight" style={heading}>{shortenAddress(op.departure)}</p>
                      <div className="h-5" />
                      <p className="text-sm font-semibold text-zinc-800 truncate leading-tight" style={heading}>{shortenAddress(op.arrival)}</p>
                    </div>
                  </div>
                </div>

                {/* Info row */}
                <div className="flex items-center gap-3 pt-3 border-t border-zinc-100">
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtShort(op.deliveryDate)}</span>
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Truck className="w-3 h-3" />{getVehicleTypeLabel(op.vehicleType)} {op.weight}T</span>
                  {op.distance_km && <span className="text-[11px] text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{Math.round(op.distance_km)} km</span>}
                  {permissions.canViewPrices && op.estimatedPrice && (
                    <span className="text-[11px] font-semibold text-zinc-700 ml-auto">{op.estimatedPrice?.toLocaleString('fr-FR')} XOF</span>
                  )}
                </div>

                {/* Lot 3: Progress */}
                <div className="mt-3"><ProgressBar step={st.step} total={6} /></div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between mt-6 pt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-500">Afficher</span>
            <Select value={itemsPerPage.toString()} onValueChange={v => setItemsPerPage(Number(v))}>
              <SelectTrigger className="w-[70px] h-8 text-sm rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-zinc-500" style={mono}>{(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} sur {filtered.length}</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0 rounded-lg"><ChevronsLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0 rounded-lg"><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <span className="px-3 text-sm font-medium text-zinc-700" style={mono}>{currentPage}/{totalPages || 1}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="h-8 w-8 p-0 rounded-lg"><ChevronRight className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 p-0 rounded-lg"><ChevronsRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOperations;
